import React, { useState } from "react";
import {
  Table,
  Tag,
  Button,
  Row,
  Select,
  Card,
  Typography,
  DatePicker,
  Statistic,
  Col,
  Tooltip,
} from "antd";
import {
  DollarOutlined,
  ShoppingOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import { useQuery } from "@apollo/client";
import type { ColumnsType } from "antd/es/table";
import { GET_ORDERS, GET_CLIENTS, GET_LOCATIONS, GET_ORDERS_INDICATORS } from "./requests";
import LoadingSpinner from "../../components/loading";
import { orderStatus, typesUsers } from "../../utils/constants";
import moment from "moment-timezone";
import { Container } from "./elements";
import ViewDetailModal from "./viewDetail";
import ProductList from "../../components/productsList";
import InvoiceModal from "./modals/invoice-modal";
import { order as orderType } from "../../types";
import { utils, writeFile } from "xlsx";
import { client } from "../../graphql/client";
import { useUser } from "../../providers/user";
import type { Dayjs } from "dayjs";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const cardStyle = {
  borderRadius: 12,
  boxShadow: "0 3px 6px rgba(0, 0, 0, 0.05)",
};

const History: React.FC = () => {
  const { user } = useUser();
  const [visible, setVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(false);
  const [status, setStatus] = useState(null);
  const [requiredInvoice, setRequiredInvoice] = useState(null);
  const [existsInvoice, setExistsInvoice] = useState(null);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [order, setOrder] = useState<typeof orderType.order | null>(null);
  const [locationSearch, setLocationSearch] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [pageConfig, setPageConfig] = useState({
    page: 1,
    pageSize: 10,
  });

  const { loading, error, data, refetch } = useQuery(GET_ORDERS, {
    variables: {
      params: pageConfig,
      status: { eq: status },
      excludeStatus: { nin: [orderStatus.created.value] },
      client: { eq: selectedClient },
      location: { eq: selectedLocation },
      ...(requiredInvoice !== null && { requiredInvoice: requiredInvoice }),
      ...(existsInvoice !== null && { existsInvoice: existsInvoice }),
      ...(dateRange && {
        startDate: dateRange[0]?.toISOString(),
        endDate: dateRange[1]?.toISOString(),
      }),
    },
  });

  const { data: indicatorsData } = useQuery(GET_ORDERS_INDICATORS, {
    variables: {
      status: { eq: status },
      excludeStatus: { nin: [orderStatus.created.value] },
      client: selectedClient ? { eq: selectedClient } : null,
      location: selectedLocation ? { eq: selectedLocation } : null,
      ...(dateRange && {
        startDate: dateRange[0]?.toISOString(),
        endDate: dateRange[1]?.toISOString(),
      }),
    },
  });


  const { data: clientsData } = useQuery(GET_CLIENTS, {
    variables: {
      params: { page: 1,
        pageSize: 5 },
      search: clientSearch,
    },
  });

  const { data: locationsData } = useQuery(GET_LOCATIONS, {
    variables: {
      params: { page: 1,
        pageSize: 5 },
      name: locationSearch,
    },
  });

  const filteredOptions = clientsData?.clients?.results.map((client: any) => ({
    label: `${client.user?.firstName} ${client.user?.lastName}`,
    value: client.id,
  }));

  const filteredLocationsOptions = locationsData?.locations?.results.map((location: any) => ({
    label: `${location.name}`,
    value: location.id,
  }));

  if (error) return <div>Error: {error.message}</div>;

  const columns: ColumnsType<any> = [
    {
      title: "Cliente",
      key: "client",
      render: (record) => (
        <div>
          <Text style={{ fontWeight: "700" }}>
            {record.client?.user?.firstName} {record?.client?.user.lastName}
          </Text>
          <br />
          <Text style={{ color: "grey" }}>{record?.reference}</Text>
        </div>
      ),
    },
    {
      title: "Creado",
      key: "createdAt",
      render: (record) => (
        <Text style={{ color: "grey" }}>
          {moment(record.updatedAt).format("lll")}
        </Text>
      ),
    },
    {
      title: "Estatus",
      key: "status",
      render: (record) => (
        <>
          <Tag color={orderStatus[record.status].color}>
            {orderStatus[record.status].name}
          </Tag>
          <br />
          <Text style={{ color: "grey" }}>
            {record.status == "complete"
              ? moment(record.updatedAt).format("lll")
              : "Sin completar"}
          </Text>
        </>
      ),
    },
    {
      title: "Productos",
      key: "products",
      dataIndex: "lineItems",
      render: (lineItems: any[]) => {
        const products =
          lineItems?.map((item) => ({
            product: {
              _id: item.product.id,
              img: item.product.img,
              title: item.product.title,
              description: item.product.description,
              product: {
                _id: item.product.product.id,
                name: item.product.product.name,
                description: item.product.product.description,
              },
            },
          })) ?? [];

        if (products.length > 0) {
          return (
            <div
              style={{ maxHeight: "200px",
                overflowY: "auto",
                width: "100%" }}
            >
              <ProductList lineItems={products} />
            </div>
          );
        } else {
          return <Tag color="yellow">Sin Productos</Tag>;
        }
      },
    },
    {
      title: "Total",
      key: "total",
      render: (record) => (
        <>
          <Tag
            color="green"
            style={{ fontSize: 16,
              padding: 5,
              fontWeight: "800" }}
          >
            ${record.price.toLocaleString()}{" "}
            <Text style={{ fontSize: 8,
              color: "green" }}>MXN</Text>
          </Tag>
        </>
      ),
    },
    {
      title: "Franquicia",
      key: "location",
      render: (record) => (
        <Text style={{ color: "green",
          fontSize: 16,
          fontWeight: "700" }}>{record.location.name}</Text>
      ),
    },
    {
      title: "Requiere Factura",
      key: "requiredInvoice",
      width: 120,
      render: (row) => {
        const existsInvoice = row?.invoice;
        if(existsInvoice){
          return <Tag color="green">Factura emitida</Tag>
        } else {
          return (
            <Text style={{ fontWeight: "500" }}>{row?.requiredInvoice ? "Sí" : "No"}</Text>
          )
        }
      },
    },
    {
      title: "Acciones",
      key: "actions",
      render: (row) => (
        <>
          <Button
            onClick={() => {
              setVisible(true);
              setSelectedOrder(row);
            }}
            type="primary"
          >
            Ver Detalle
          </Button>
          <Tooltip title={!row.requiredInvoice ? null : "Factura"}>
            <Button
              type="primary"
              style={{ marginLeft:5 }}
              icon={<FileTextOutlined />}
              disabled={!row.requiredInvoice}
              onClick={()=>{
                setOrder(row)
                setShowInvoiceModal(true);
              }}
            />
          </Tooltip>
        </>
      ),
    },
  ];

  const exportOrders = async () => {
    setExportLoading(true);
    try {
      const { data } = await client.query({
        query: GET_ORDERS,
        variables: {
          params: { page: 1,
            pageSize: 1000 },
          status: { eq: status },
          client: { eq: selectedClient },
          location: { eq: selectedLocation },
          ...(requiredInvoice !== null && { requiredInvoice: requiredInvoice }),
          ...(existsInvoice !== null && { existsInvoice: existsInvoice }),
          ...(dateRange && {
            startDate: dateRange[0]?.toISOString(),
            endDate: dateRange[1]?.toISOString(),
          }),
        },
      });

      const headers = [
        [
          "REFERENCIA",
          "ESTATUS",
          "TOTAL",
          "FECHA COMPLETADA",
          "REQU. FACTURA",
          "CLIENTE",
          "NOMBRE FISCAL",
          "RFC",
          "EMAIL",
          "REGIMEN FISCAL",
          "USO CFDI",
          "PAIS",
          "ESTADO",
          "CIUDAD",
          "CALLE",
          "CP",
          "COLONIA",
          "NUM. INTERIOR",
          "NUM. EXTERIOR",
        ],
      ];

      const parcelsArr = data?.orders?.results?.map((order) => {
        const fiscal = order.client?.fiscalData;
        const address = fiscal?.address;

        return {
          REFERENCIA: order.reference ?? "N/A",
          ESTATUS: orderStatus[order.status].name ?? "N/A",
          TOTAL: order.price ?? 0,
          "FECHA COMPLETADA": order.updatedAt
            ? moment(order.updatedAt).format("lll")
            : "N/A",
          "REQU. FACTURA": order.requiredInvoice ? "Sí" : "No",
          CLIENTE: `${order.client?.user?.firstName ?? ""} ${order.client?.user?.lastName ?? ""}`,
          "NOMBRE FISCAL": fiscal?.name ?? "N/A",
          RFC: fiscal?.rfc ?? "N/A",
          EMAIL: fiscal?.email ?? "N/A",
          "REGIMEN FISCAL": fiscal?.regimenFiscal ?? "N/A",
          "USO CFDI": fiscal?.usoCFDI ?? "N/A",
          PAIS: address?.country ?? "N/A",
          ESTADO: address?.state ?? "N/A",
          CIUDAD: address?.city ?? "N/A",
          CALLE: address?.street ?? "N/A",
          CP: address?.postcode ?? "N/A",
          COLONIA: address?.colony ?? "N/A",
          "NUM. INTERIOR": address?.internalNumber ?? "N/A",
          "NUM. EXTERIOR": address?.exteriorNumber ?? "N/A",
        };
      });

      const wb = utils.book_new();
      const ws = utils.json_to_sheet([]);
      utils.sheet_add_aoa(ws, headers);
      utils.sheet_add_json(ws, parcelsArr, { origin: "A2",
        skipHeader: true });
      utils.book_append_sheet(wb, ws, "Sheet1");
      const date = moment().format("lll");
      writeFile(wb, `Ordenes-${date}.xlsx`);
    } catch (err) {
      console.log(err);
    }
    setExportLoading(false);
  };

  return (
    <Container style={{ padding: "20px",
      overflow: "scroll",
      height: "90vh" }}>
      {user?.type !== typesUsers.manager.value && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={false} style={{ ...cardStyle }}>
              <Statistic
                title={`Ordenes Totales ${!dateRange ? "del Mes" : ""}`}
                value={indicatorsData?.ordersIndicators.totalOrders || 0}
                prefix={<ShoppingOutlined style={{ color: "#e67e22" }} />}
                suffix="🔥"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={false} style={{ ...cardStyle }}>
              <Statistic
                title={`Ordenes Pendientes por Surtir ${!dateRange ? "del Mes" : ""}`}
                value={indicatorsData?.ordersIndicators.pendingOrders || 0}
                prefix={<ClockCircleOutlined style={{ color: "#f1c40f" }} />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card loading={false} style={{ ...cardStyle }}>
              <Statistic
                title={`Ordenes Completadas ${!dateRange ? "del Mes" : ""}`}
                value={indicatorsData?.ordersIndicators.completedOrders || 0}
                prefix={<CheckCircleOutlined style={{ color: "#3498db" }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card loading={false} style={{ ...cardStyle }}>
              <Statistic
                title={`Ingresos ${!dateRange ? "del Mes" : ""}`}
                value={indicatorsData?.ordersIndicators.ingresos || 0}
                precision={2}
                prefix={<DollarOutlined style={{ color: "#2ecc71" }} />}
              />
            </Card>
          </Col>
        </Row>
      )}
      <Card
        style={{
          width: "100%",
          borderRadius: 10,
          overflow: "hidden",
          boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
        }}
        bodyStyle={{ padding: 0 }}
      >
        <Row style={{ padding: 20 }}>
          <div>
            <Text style={{ fontWeight: "700",
              fontSize: 18 }}>
              Rango de Fechas
            </Text>
            <br />{" "}
            <RangePicker
              style={{ width: "200px" }}
              onChange={(e) => setDateRange(e)}
              value={dateRange}
              placeholder={["Comienzo", "Fin"]}
            />
          </div>
          <div style={{ marginLeft: 10 }}>
            <Text style={{ fontWeight: "700",
              fontSize: 18 }}>
              Filtrar Cliente
            </Text>
            <br />
            <Select
              showSearch
              style={{ width: "200px" }}
              placeholder="Cliente"
              onSearch={(value) => setClientSearch(value)}
              onChange={(value) => setClientSearch(value)}
              onSelect={(value) => setSelectedClient(value)}
              onClear={() => setSelectedClient(null)}
              allowClear
              options={filteredOptions}
              filterOption={false}
            />
          </div>
          <div style={{ marginLeft: 10 }}>
            <Text style={{ fontWeight: "700",
              fontSize: 18 }}>
              Filtrar Estatus
            </Text>
            <br />
            <Select
              style={{ width: "200px" }}
              placeholder="Estatus "
              onChange={(value) => setStatus(value)}
              onClear={() => setStatus(null)}
              allowClear
            >
              {Object.keys(orderStatus)
                .filter((key) => key !== "created")
                .map((key) => (
                  <Select.Option key={key} value={key}>
                    <Tag color={orderStatus[key].color}>
                      {orderStatus[key].name}
                    </Tag>
                  </Select.Option>
                ))}
            </Select>{" "}
          </div>
          <div style={{ marginLeft: 10 }}>
            <Text style={{ fontWeight: "700",
              fontSize: 18 }}>
              Filtrar Franquicia
            </Text>
            <br />

            <Select
              showSearch
              allowClear
              style={{ width: "200px" }}
              placeholder="Franquicia"
              onSearch={(value) => setLocationSearch(value)}
              onSelect={(value) => setSelectedLocation(value)}
              onClear={() => { setSelectedLocation(null); setLocationSearch(""); }}
              value={selectedLocation}
              options={filteredLocationsOptions}
              filterOption={false}
            />
          </div>
          <div style={{ marginLeft: 10 }}>
            <Text style={{ fontWeight: "700",
              fontSize: 18 }}>
              Requiere Factura
            </Text>
            <br />
            <Select
              style={{ width: "200px" }}
              placeholder="Requiere factura "
              onChange={(value) => {
                if(value !== undefined){
                  setRequiredInvoice(value);
                } else {
                  setRequiredInvoice(null);
                }
              }}
              allowClear
            >
              <Select.Option value={true}>Sí</Select.Option>
              <Select.Option value={false}>No</Select.Option>
            </Select>
          </div>
          <div style={{ marginLeft: 10 }}>
            <Text style={{ fontWeight: "700",
              fontSize: 18 }}>
              Factura Emitida
            </Text>
            <br />
            <Select
              style={{ width: "200px" }}
              placeholder="Factura Emitida"
              onChange={(value) => {
                if(value !== undefined){
                  setExistsInvoice(value);
                } else {
                  setExistsInvoice(null);
                }
              }}
              allowClear
            >
              <Select.Option value={true}>Sí</Select.Option>
              <Select.Option value={false}>No</Select.Option>
            </Select>
          </div>
          <div style={{ marginLeft: 30,
            marginTop: 15 }}>
            <Button
              type="primary"
              size="large"
              loading={exportLoading}
              icon={<FileExcelOutlined />}
              onClick={exportOrders}
            >
              Exportar
            </Button>
          </div>
        </Row>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div style={{ marginTop: 10 }}>
              <Table
                dataSource={data?.orders?.results || []}
                columns={columns}
                rowKey="id"
                pagination={{
                  current: Number(pageConfig.page),
                  defaultCurrent: pageConfig.page,
                  pageSize: Number(pageConfig.pageSize),
                  defaultPageSize: pageConfig.pageSize,
                  total: data?.orders?.info?.count,
                  showTotal: (total) => <Text disabled>{`${total} resultados`}</Text>,
                  onChange: (page, pageSize) => {
                    setPageConfig({
                      page,
                      pageSize,
                    });
                  },
                  showSizeChanger: true,
                }}
              />
              <ViewDetailModal
                visible={visible}
                setVisible={setVisible}
                selectedOrder={selectedOrder}
              />
              <InvoiceModal
                showModal={showInvoiceModal}
                setShowModal={setShowInvoiceModal}
                refetch={refetch}
                order={order}
              />
            </div>
          </>
        )}
      </Card>
    </Container>
  );
};

export default History;
